// React
import React, { useState, useEffect } from 'react';

// Material UI
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { preorderEventsSetFilters } from '../../../actions/preorderEventActions/PreorderEventActions';
import { RootStore } from '../../../Store';

// Translations
import { useTranslation } from 'react-i18next';

// Styles
import useStyles from './PreordersFiltersStyles';

interface PreordersFiltersProps {
  onSearch: () => void;
  onClear: () => void;
}

const PreordersFilters: React.FC<PreordersFiltersProps> = ({ onSearch, onClear }) => {
  const classes = useStyles();
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventFilters } = preorderEventState;

  const [name, setName] = useState<string>('');
  const [releaseDate, setReleaseDate] = useState<string>('');
  const [lastPreorderDate, setLastPreorderDate] = useState<string>('');

  useEffect(() => {
    // Keep local state in sync if filters are externally cleared
    if (!preorderEventFilters || Object.keys(preorderEventFilters).length === 0) {
      setName('');
      setReleaseDate('');
      setLastPreorderDate('');
    }
  }, [preorderEventFilters]);

  const handleSearch = () => {
    dispatch(preorderEventsSetFilters({
      name: name || undefined,
      releaseDate: releaseDate || undefined,
      lastPreorderDate: lastPreorderDate || undefined,
      page: 0,
      size: 10
    }));
    onSearch();
  };

  const handleClear = () => {
    dispatch(preorderEventsSetFilters({}));
    onClear();
  };

  return (
    <Paper className={classes.root} elevation={3}>
      <div className={classes.boxTop}>
        <SearchIcon color="primary" />
        <Typography variant="h6" color="primary">{t('preorders.preorders-filter')}</Typography>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            label={t('preorders.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            label={t('preorders.release-date')}
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            label={t('preorders.last-preorder-date')}
            type="date"
            value={lastPreorderDate}
            onChange={(e) => setLastPreorderDate(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              {t('preorders.search')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
            >
              {t('preorders.clear')}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PreordersFilters;
